import { UserFriendlyError } from '@affine/error';
import {
  deleteBlobMutation,
  listBlobsQuery,
  releaseDeletedBlobsMutation,
  setBlobMutation,
  workspaceBlobQuotaQuery,
} from '@affine/graphql';
import * as tus from 'tus-js-client';

import {
  type BlobRecord,
  BlobStorageBase,
  getBlobRecordSize,
  OverCapacityError,
  OverSizeError,
} from '../../storage';
import { HttpConnection } from './http';
import { resolveCloudHttpBaseUrl, resolveCloudHttpEndpoint } from './url';

interface CloudBlobStorageOptions {
  serverBaseUrl: string;
  id: string;
  /**
   * Blob sync must only mirror metadata into local IndexedDB. Explicit direct
   * readers such as workspace avatars opt in to byte downloads.
   */
  downloadBytes?: boolean;
}

const SHOULD_MANUAL_REDIRECT = BUILD_CONFIG.isAndroid || BUILD_CONFIG.isIOS;

export class CloudBlobStorage extends BlobStorageBase {
  static readonly identifier = 'CloudBlobStorage';
  override readonly isReadonly = false;

  constructor(private readonly options: CloudBlobStorageOptions) {
    super();
  }

  private readonly serverBaseUrl = resolveCloudHttpBaseUrl(
    this.options.serverBaseUrl
  );

  readonly connection = new HttpConnection(this.serverBaseUrl);

  private static readonly MAX_DIRECT_DOWNLOAD_SIZE = 100 * 1024 * 1024; // 100 MB

  override async get(key: string, signal?: AbortSignal) {
    if (!this.options.downloadBytes) {
      return await this.getMetadataOnly(key, signal);
    }

    const res = await this.connection.fetch(
      '/api/workspaces/' +
        this.options.id +
        '/blobs/' +
        key +
        (SHOULD_MANUAL_REDIRECT ? '?redirect=manual' : ''),
      {
        cache: 'default',
        headers: {
          'x-streamlain-version': BUILD_CONFIG.appVersion,
        },
        signal,
      }
    );

    if (res.status === 404) {
      return null;
    }

    // sl-rm09.4: Blob access denied (doc-level RBAC) — treat as not found
    // No retry loop: 403 is a permanent authorization failure
    if (res.status === 403) {
      return null;
    }

    try {
      const contentLength = Number(res.headers.get('content-length') || 0);
      if (contentLength > CloudBlobStorage.MAX_DIRECT_DOWNLOAD_SIZE) {
        return {
          key,
          mime: res.headers.get('content-type') || 'application/octet-stream',
          size: contentLength,
          createdAt: new Date(res.headers.get('last-modified') || Date.now()),
        };
      }

      const contentType = res.headers.get('content-type');

      let blob;

      if (
        SHOULD_MANUAL_REDIRECT &&
        contentType?.startsWith('application/json')
      ) {
        const json = await res.json();
        if ('url' in json && typeof json.url === 'string') {
          const res = await this.connection.fetch(json.url, {
            cache: 'default',
            headers: {
              'x-streamlain-version': BUILD_CONFIG.appVersion,
            },
            signal,
          });

          blob = await res.blob();
        } else {
          throw new Error('Invalid blob response');
        }
      } else {
        blob = await res.blob();
      }

      return {
        key,
        data: new Uint8Array(await blob.arrayBuffer()),
        mime: blob.type,
        size: blob.size,
        createdAt: new Date(res.headers.get('last-modified') || Date.now()),
      };
    } catch (err) {
      throw new Error('blob download error: ' + err);
    }
  }

  private async getMetadataOnly(key: string, signal?: AbortSignal) {
    const res = await this.connection.fetch(
      `/api/workspaces/${this.options.id}/blobs/${key}/info`,
      {
        cache: 'default',
        headers: {
          'x-streamlain-version': BUILD_CONFIG.appVersion,
        },
        signal,
      }
    );

    if (res.status === 404) {
      return null;
    }

    const info = (await res.json()) as Partial<{
      key: string;
      mimeType: string;
      size: number;
      createdAt: string;
    }>;
    if (
      typeof info.key !== 'string' ||
      typeof info.mimeType !== 'string' ||
      typeof info.size !== 'number' ||
      !Number.isSafeInteger(info.size) ||
      info.size < 0
    ) {
      throw new Error(`Invalid blob metadata response for ${key}`);
    }

    return {
      key: info.key,
      mime: info.mimeType,
      size: info.size,
      createdAt: new Date(info.createdAt || Date.now()),
    };
  }

  override async set(blob: BlobRecord, signal?: AbortSignal) {
    try {
      const quota = await this.getQuotaConfig();

      if (getBlobRecordSize(blob) > quota.blobLimit) {
        throw new OverSizeError(this.humanReadableBlobSizeLimitCache);
      }

      if (quota.tusUploadEnabled && quota.tusUploadPath) {
        await this.setViaTus(blob, quota.tusUploadPath, signal);
      } else {
        await this.setViaGraphQL(blob, signal);
      }
    } catch (err) {
      if (err instanceof OverSizeError || err instanceof OverCapacityError) {
        throw err;
      }
      const userFriendlyError = UserFriendlyError.fromAny(err);
      if (userFriendlyError.is('STORAGE_QUOTA_EXCEEDED')) {
        throw new OverCapacityError();
      }
      if (userFriendlyError.is('BLOB_QUOTA_EXCEEDED')) {
        throw new OverSizeError(this.humanReadableBlobSizeLimitCache);
      }
      if (userFriendlyError.is('CONTENT_TOO_LARGE')) {
        throw new OverSizeError(
          null,
          'Upload stopped by network proxy: file size exceeds the set limit.'
        );
      }
      throw err;
    }
  }

  /**
   * Classic upload via GraphQL setBlobMutation (small files, tusEnabled=false).
   */
  private async setViaGraphQL(
    blob: BlobRecord,
    signal?: AbortSignal
  ): Promise<void> {
    const source = this.getUploadSource(blob);
    await this.connection.gql({
      query: setBlobMutation,
      variables: {
        workspaceId: this.options.id,
        blob: new File([source], blob.key, {
          type: blob.mime,
        }),
      },
      context: { signal },
    });
  }

  /**
   * Resumable upload via tus protocol (large files, tusEnabled=true).
   *
   * tus-js-client handles chunking, retry on network failure, and resume
   * after disconnect. The backend POST_FINISH hook delegates to BlobService.setBlob()
   * ensuring identical storage behavior.
   */
  private setViaTus(
    blob: BlobRecord,
    tusPath: string,
    signal?: AbortSignal
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const file = this.getUploadSource(blob);
      let returnedBlobKey: string | null = null;

      const upload = new tus.Upload(file, {
        endpoint: resolveCloudHttpEndpoint(this.serverBaseUrl, tusPath),
        retryDelays: [0, 1000, 3000, 5000, 10000],
        metadata: {
          workspaceId: this.options.id,
          uploadMode: 'client-keyed',
          blobKey: blob.key,
          mimeType: blob.mime || 'application/octet-stream',
        },
        // Send session cookie for auth (tus uses XMLHttpRequest in browsers)
        onBeforeRequest: req => {
          const xhr = req.getUnderlyingObject();
          if (xhr && 'withCredentials' in xhr) {
            xhr.withCredentials = true;
          }
        },
        onError: error => {
          reject(error);
        },
        onAfterResponse: (_req, res) => {
          returnedBlobKey = res.getHeader('X-Blob-Key') ?? returnedBlobKey;
        },
        onSuccess: () => {
          if (returnedBlobKey && returnedBlobKey !== blob.key) {
            reject(
              new Error(
                `TUS client-keyed upload returned mismatched blob key ${returnedBlobKey}`
              )
            );
            return;
          }
          resolve();
        },
      });

      if (signal) {
        signal.addEventListener('abort', () => {
          upload.abort(true).catch(() => {});
          reject(new DOMException('Upload aborted', 'AbortError'));
        });
      }

      upload.start();
    });
  }

  private getUploadSource(blob: BlobRecord): Blob {
    if (blob.source) return blob.source;
    if (blob.data && blob.data.byteLength > 0) {
      return new Blob([blob.data], {
        type: blob.mime || 'application/octet-stream',
      });
    }
    if (getBlobRecordSize(blob) === 0) {
      return new Blob([], {
        type: blob.mime || 'application/octet-stream',
      });
    }
    throw new Error(
      `Blob ${blob.key} has metadata but no uploadable byte source`
    );
  }

  override async delete(key: string, permanently: boolean) {
    await this.connection.gql({
      query: deleteBlobMutation,
      variables: { workspaceId: this.options.id, key, permanently },
    });
  }

  override async release() {
    await this.connection.gql({
      query: releaseDeletedBlobsMutation,
      variables: { workspaceId: this.options.id },
    });
  }

  override async list() {
    const res = await this.connection.gql({
      query: listBlobsQuery,
      variables: { workspaceId: this.options.id },
    });

    return res.workspace.blobs.map(blob => ({
      ...blob,
      createdAt: new Date(blob.createdAt),
    }));
  }

  // ---------------------------------------------------------------------------
  // Quota cache (120s TTL)
  // ---------------------------------------------------------------------------

  private humanReadableBlobSizeLimitCache: string | null = null;
  private quotaCache: {
    blobLimit: number;
    tusUploadEnabled: boolean;
    tusUploadPath: string | null;
  } | null = null;
  private quotaCacheTime = 0;

  private async getQuotaConfig() {
    if (
      this.quotaCache !== null &&
      Date.now() - this.quotaCacheTime < 120 * 1000
    ) {
      return this.quotaCache;
    }
    try {
      const res = await this.connection.gql({
        query: workspaceBlobQuotaQuery,
        variables: { id: this.options.id },
      });

      this.humanReadableBlobSizeLimitCache =
        res.workspace.quota.humanReadable.blobLimit;
      this.quotaCache = {
        blobLimit: res.workspace.quota.blobLimit,
        tusUploadEnabled: res.workspace.quota.tusUploadEnabled,
        tusUploadPath: res.workspace.quota.tusUploadPath,
      };
      this.quotaCacheTime = Date.now();
      return this.quotaCache;
    } catch (err) {
      throw UserFriendlyError.fromAny(err);
    }
  }
}
